// src/services/marketHeatHistory.ts

/**
 * Service for persisting and retrieving Market Heat Score history
 * Uses Upstash Redis sorted sets for efficient time-range queries
 */

import {
  zaddToCache,
  zrangeByScore,
  zremRangeByScore,
  zrevrangeWithScores
} from './cache';
import { MARKET_HEAT_HISTORY } from '../constants';
import { MarketHeatHistoryEntry, HistoryTimeRange, MarketHeatScore } from '../types';

// ETH price history entry
export interface EthPriceHistoryEntry {
  price: number;
  timestamp: number;
}

// Time range in milliseconds (0 = all time)
const TIME_RANGES: Record<HistoryTimeRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
  '1y': 365 * 24 * 60 * 60 * 1000,
  'all': 0, // Special value for all time
};

/**
 * Check if we should persist a new entry based on time and score change
 */
export const shouldPersist = (
  currentScore: number,
  lastEntry: MarketHeatHistoryEntry | null
): boolean => {
  if (!lastEntry) {
    return true; // No previous entry, always persist
  }

  const timeSinceLastEntry = Date.now() - lastEntry.timestamp;
  const scoreDiff = Math.abs(currentScore - lastEntry.overall);

  // Always persist if enough time has passed
  if (timeSinceLastEntry >= MARKET_HEAT_HISTORY.MIN_PERSIST_INTERVAL_MS) {
    return true;
  }

  // Persist if score changed significantly (even within the time window)
  if (scoreDiff >= MARKET_HEAT_HISTORY.MIN_SCORE_CHANGE) {
    return true;
  }

  return false;
};

/**
 * Get the most recent history entry
 */
export const getLatestHistoryEntry = async (): Promise<MarketHeatHistoryEntry | null> => {
  try {
    const entries = await zrevrangeWithScores<MarketHeatHistoryEntry>(
      MARKET_HEAT_HISTORY.CACHE_KEY,
      0,
      0
    );

    if (entries.length > 0) {
      return entries[0].member;
    }

    return null;
  } catch (error) {
    console.error('Error getting latest history entry:', error);
    return null;
  }
};

/**
 * Persist a market heat score to history
 */
export const persistMarketHeatScore = async (
  score: MarketHeatScore
): Promise<boolean> => {
  try {
    // Check if we should persist
    const lastEntry = await getLatestHistoryEntry();

    if (!shouldPersist(score.overall, lastEntry)) {
      console.log('Skipping history persist - too recent or no significant change');
      return false;
    }

    // Create history entry
    const entry: MarketHeatHistoryEntry = {
      overall: score.overall,
      interpretation: score.interpretation,
      timestamp: score.timestamp || Date.now(),
    };

    // Add to sorted set with timestamp as score
    const success = await zaddToCache(
      MARKET_HEAT_HISTORY.CACHE_KEY,
      entry.timestamp,
      JSON.stringify(entry)
    );

    if (success) {
      console.log(`Market heat score persisted: ${entry.overall} (${entry.interpretation})`);

      // Clean up old entries (older than retention period)
      await cleanupOldEntries();
    }

    return success;
  } catch (error) {
    console.error('Error persisting market heat score:', error);
    return false;
  }
};

/**
 * Get the start of today (midnight) in milliseconds
 */
const getStartOfToday = (): number => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
};

/**
 * Get market heat history for a specific time range
 * Excludes today's data - only shows completed days (from daily Telegram reports)
 */
export const getMarketHeatHistory = async (
  timeRange: HistoryTimeRange
): Promise<MarketHeatHistoryEntry[]> => {
  try {
    const startOfToday = getStartOfToday();
    const rangeMs = TIME_RANGES[timeRange];
    // For 'all' (0), use timestamp 0 to get everything
    const minTimestamp = rangeMs === 0 ? 0 : startOfToday - rangeMs;

    // Fetch up to start of today (exclude today's data)
    const entries = await zrangeByScore<MarketHeatHistoryEntry>(
      MARKET_HEAT_HISTORY.CACHE_KEY,
      minTimestamp,
      startOfToday - 1 // Exclude today
    );

    console.log(`Retrieved ${entries.length} history entries for ${timeRange} (excluding today)`);
    return entries;
  } catch (error) {
    console.error(`Error getting market heat history for ${timeRange}:`, error);
    return [];
  }
};

/**
 * Clean up entries older than the retention period
 */
export const cleanupOldEntries = async (): Promise<boolean> => {
  try {
    const cutoffTimestamp = Date.now() - (MARKET_HEAT_HISTORY.RETENTION_DAYS * 24 * 60 * 60 * 1000);

    const success = await zremRangeByScore(
      MARKET_HEAT_HISTORY.CACHE_KEY,
      0,
      cutoffTimestamp
    );

    if (success) {
      console.log(`Cleaned up history entries older than ${MARKET_HEAT_HISTORY.RETENTION_DAYS} days`);
    }

    return success;
  } catch (error) {
    console.error('Error cleaning up old history entries:', error);
    return false;
  }
};

/**
 * Simple persist function for use in cron jobs (Node.js environment)
 * This is a standalone version that doesn't depend on the cache service
 */
export const persistToRedisDirectly = async (
  score: { overall: number; interpretation: string; timestamp?: number },
  redisUrl: string,
  redisToken: string
): Promise<boolean> => {
  try {
    const entry: MarketHeatHistoryEntry = {
      overall: score.overall,
      interpretation: score.interpretation,
      timestamp: score.timestamp || Date.now(),
    };

    const response = await fetch(
      `${redisUrl}/zadd/${MARKET_HEAT_HISTORY.CACHE_KEY}/${entry.timestamp}/${encodeURIComponent(JSON.stringify(entry))}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${redisToken}`
        }
      }
    );

    if (response.ok) {
      console.log(`Market heat score persisted to history: ${entry.overall}`);
      return true;
    } else {
      console.error('Failed to persist market heat score:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error persisting to Redis directly:', error);
    return false;
  }
};

/**
 * Fetch ETH price history from CoinGecko for a given time range
 */
export const getEthPriceHistory = async (
  timeRange: HistoryTimeRange
): Promise<EthPriceHistoryEntry[]> => {
  try {
    // Map time range to days for CoinGecko API
    const daysMap: Record<HistoryTimeRange, number> = {
      '24h': 1,
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365,
      'all': 365, // Max for free API
    };

    const days = daysMap[timeRange];
    const interval = days <= 1 ? 'hourly' : 'daily';

    console.log(`Fetching ETH price history for ${timeRange} (${days} days)`);

    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/ethereum/market_chart?vs_currency=usd&days=${days}&interval=${interval}`
    );

    if (!response.ok) {
      throw new Error(`CoinGecko API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.prices || !Array.isArray(data.prices)) {
      throw new Error('Invalid response from CoinGecko');
    }

    const entries: EthPriceHistoryEntry[] = data.prices.map(
      ([timestamp, price]: [number, number]) => ({
        timestamp,
        price,
      })
    );

    console.log(`Retrieved ${entries.length} ETH price entries`);
    return entries;
  } catch (error) {
    console.error(`Error fetching ETH price history:`, error);
    return [];
  }
};
